import { Link as RouterLink } from "react-router-dom";
import { Alert, AlertIcon, Badge, Box, Heading, HStack, Link, Spinner, Text, VStack } from "@chakra-ui/react";
import { useState } from "react";
import { usePlacement } from "../../hooks/usePlacement";
import CarrierSubmissionView from "./CarrierSubmissionView";
import PlacementOverview from "./PlacementOverview";
import { displayStageCode } from "./placingBook";
import { linkedIds } from "../../services/PlacingAPI";

/**
 * One placement's whole placing process, for the deal tab and the placing
 * portal. A broker gets the single-pane overview (stages, then carriers);
 * a carrier gets the same screens as the submission page.
 */
const PlacementWorkspace = ({ placementId, showHeader = true, editorLink, showDealLink }) => {
  // Refreshes every minute like the placing portal, but not while a form is open
  const [paused, setPaused] = useState(false);
  const data = usePlacement({ placementId, autoRefresh: true, paused });
  const { loading, error, placement, submission, role, reload } = data;

  if (loading) return <Spinner />;
  if (role === "broker") return <PlacementOverview data={data} editorLink={editorLink} showDealLink={showDealLink} onPausedChange={setPaused} />;
  if (error) return <Alert status="error"><AlertIcon />{error.message || "Couldn't load placement."}</Alert>;
  if (!placement) return <Alert status="warning"><AlertIcon />Placement not found.</Alert>;

  return (
    <VStack align="stretch" spacing={4}>
      {showHeader && (
        <Box borderWidth="1px" borderRadius="md" p={4} bg="whiteAlpha.800">
          <HStack justify="space-between" flexWrap="wrap" rowGap={2}>
            <HStack spacing={3}>
              <Heading size="md">{placement.name || "Placement"}</Heading>
              <Badge>{displayStageCode(placement.placingStageCode, linkedIds(placement.quoteRequests).length > 0)}</Badge>
              {placement.status && <Badge colorScheme="gray" variant="outline">{placement.status}</Badge>}
            </HStack>
            <HStack spacing={4} fontSize="sm">
              {placement.productCode && <Text color="gray.500">Product {placement.productCode}</Text>}
              {placement.documentId && (
                <Link as={RouterLink} color="blue.600" to={`/editor?doc=${placement.documentId}`}>Open document</Link>
              )}
              {placement.dealId && placement.productCode && (
                <Link as={RouterLink} color="blue.600" to={`/product/${placement.productCode}/deal/${placement.dealId}/deal`}>
                  Open deal
                </Link>
              )}
            </HStack>
          </HStack>
          {placement.notes && (
            <Text fontSize="sm" color="gray.700" mt={2} whiteSpace="pre-wrap">{placement.notes}</Text>
          )}
        </Box>
      )}
      {!submission ? (
        <Alert status="info"><AlertIcon />This placement has no submission yet.</Alert>
      ) : (
        <CarrierSubmissionView submission={submission} onChanged={reload} />
      )}
    </VStack>
  );
};

export default PlacementWorkspace;
